import type { ApiError } from '@riskpay/types';

/**
 * Shared fetch client for the four portals.
 *
 * Responsibilities kept deliberately small: attach the bearer token, attach an
 * Idempotency-Key to every mutating call, surface the server's structured error
 * as a typed exception, and hand a 401 back to the caller so the app can sign
 * the user out. Caching and retries belong to TanStack Query, not here.
 */

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly type: string;
  readonly param?: string;
  readonly hint?: string;
  readonly requestId?: string;

  constructor(status: number, body: ApiError | null, fallback: string) {
    super(body?.error.message ?? fallback);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = body?.error.code ?? 'network_error';
    this.type = body?.error.type ?? 'api_error';
    this.param = body?.error.param;
    this.hint = body?.error.hint;
    this.requestId = body?.error.request_id;
  }
}

export interface ApiClient {
  get<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T>;
  post<T>(path: string, body?: unknown, options?: { idempotencyKey?: string }): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
  /** Downloads a file (CSV export) and triggers a browser save. */
  download(path: string, filename: string, params?: Record<string, string | undefined>): Promise<void>;
}

/**
 * Builds the request URL.
 *
 * `baseUrl` is normally a same-origin path like `/api/v1/merchant`, which the
 * dev server proxies to the API. `new URL()` REQUIRES an absolute URL and
 * throws a TypeError on a bare path — so the page origin is supplied as the
 * base. When `baseUrl` is already absolute (a separate API host in production)
 * the base argument is ignored, which is exactly the behaviour wanted.
 */
function resolve(baseUrl: string, path: string): URL {
  const base = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
  return new URL(baseUrl.replace(/\/$/, '') + path, base);
}

export function createApiClient(config: {
  baseUrl: string;
  /** Called on every request to supply the current access token. */
  getToken: () => string | null;
  /** Called on a 401 so the app can clear its session and redirect. */
  onUnauthorized: () => void;
  /**
   * Current Test/Live toggle, sent as X-RiskPay-Mode. Read per request rather
   * than captured, so flipping the switch takes effect on the very next call
   * without rebuilding the client or re-issuing the session token.
   */
  getMode?: () => 'TEST' | 'LIVE' | null;
}): ApiClient {
  async function request<T>(
    method: string,
    path: string,
    options: { body?: unknown; params?: Record<string, string | number | undefined>; idempotencyKey?: string } = {},
  ): Promise<T> {
    const url = resolve(config.baseUrl, path);
    for (const [key, value] of Object.entries(options.params ?? {})) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }

    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = config.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const mode = config.getMode?.();
    if (mode) headers['X-RiskPay-Mode'] = mode;

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      // Every mutating call is retry-safe. crypto.randomUUID is available in
      // all target browsers; the fallback keeps dev over plain HTTP working.
      headers['Idempotency-Key'] =
        options.idempotencyKey ??
        (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        credentials: 'include',
      });
    } catch (err) {
      throw new ApiClientError(0, null, `Network request failed: ${(err as Error).message}`);
    }

    if (response.status === 401) {
      config.onUnauthorized();
      throw new ApiClientError(401, await safeJson<ApiError>(response), 'Your session has expired.');
    }

    if (response.status === 204) return undefined as T;

    const payload = await safeJson<T & ApiError>(response);
    if (!response.ok) {
      throw new ApiClientError(response.status, payload as ApiError | null, response.statusText);
    }
    return payload as T;
  }

  async function safeJson<T>(response: Response): Promise<T | null> {
    try {
      return (await response.json()) as T;
    } catch {
      return null;
    }
  }

  // Method shorthand rather than arrow properties: an arrow would be inferred
  // as returning Promise<unknown> and would not satisfy the generic signatures
  // declared on ApiClient.
  return {
    get<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
      return request<T>('GET', path, { params });
    },
    post<T>(path: string, body?: unknown, options?: { idempotencyKey?: string }): Promise<T> {
      return request<T>('POST', path, { body: body ?? {}, ...options });
    },
    patch<T>(path: string, body?: unknown): Promise<T> {
      return request<T>('PATCH', path, { body: body ?? {} });
    },
    del<T>(path: string): Promise<T> {
      return request<T>('DELETE', path);
    },

    async download(path, filename, params) {
      const url = resolve(config.baseUrl, path);
      for (const [key, value] of Object.entries(params ?? {})) {
        if (value) url.searchParams.set(key, value);
      }
      const token = config.getToken();
      const mode = config.getMode?.();
      const response = await fetch(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(mode ? { 'X-RiskPay-Mode': mode } : {}),
        },
        credentials: 'include',
      });
      if (response.status === 401) {
        config.onUnauthorized();
        return;
      }
      if (!response.ok) throw new ApiClientError(response.status, null, 'Export failed.');

      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoking immediately can cancel the download in Safari.
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    },
  };
}
